"use client";

import type {
	ComparisonCell,
	ComparisonMark,
	ComparisonRowGroup,
	SourceLink,
} from "@marketing/compare/lib/types";
import { formatLongDate } from "@marketing/shared/components/Byline";
import {
	HoverCard,
	HoverCardContent,
	HoverCardTrigger,
} from "@ui/components/hover-card";
import { cn } from "@ui/lib";
import { CheckIcon, InfoIcon, MinusIcon, XIcon } from "lucide-react";
import { Fragment } from "react";

/**
 * Two-column feature comparison for the /compare pages, generalized from the
 * HeatmapTable pattern on the data-reliability page. It stays a real <table>
 * so the facts are plain DOM text for crawlers. Below `sm` each row reflows
 * into a stacked block, with the column name printed before each cell, so
 * there's no sideways scroll on a phone.
 *
 * Marks never rely on colour: every yes / partly / no has an icon and a word.
 * Positive/negative tokens are reserved for "the metric moved", so marks use
 * primary (yes) and muted (partly, no) instead.
 */

const MARKS: Record<
	ComparisonMark,
	{ label: string; icon: typeof CheckIcon; className: string }
> = {
	yes: { label: "Yes", icon: CheckIcon, className: "text-primary" },
	partial: {
		label: "Partly",
		icon: MinusIcon,
		className: "text-muted-foreground",
	},
	no: { label: "No", icon: XIcon, className: "text-muted-foreground" },
};

function MarkedCell({ cell }: { cell: ComparisonCell }) {
	const mark = cell.mark ? MARKS[cell.mark] : null;
	return (
		<span className="flex items-start gap-2">
			{mark ? (
				<span
					className={cn(
						"mt-0.5 inline-flex shrink-0 items-center gap-1 text-xs font-bold uppercase tracking-wide",
						mark.className,
					)}
				>
					<mark.icon className="size-4" aria-hidden />
					{mark.label}
				</span>
			) : null}
			<span className="text-sm text-foreground">{cell.text}</span>
		</span>
	);
}

export function ComparisonTable({
	usLabel,
	themLabel,
	groups,
	footnote,
	sources = [],
	verified,
	className,
}: {
	usLabel: string;
	themLabel: string;
	groups: ComparisonRowGroup[];
	footnote?: string;
	/** Competitor pages the facts were checked against. */
	sources?: SourceLink[];
	/** ISO date the sources were checked. */
	verified?: string;
	className?: string;
}) {
	return (
		<div
			className={cn(
				"overflow-hidden rounded-2xl border bg-card",
				className,
			)}
		>
			<table className="w-full text-left">
				<caption className="sr-only">
					{usLabel} compared with {themLabel}, feature by feature
				</caption>
				<thead className="hidden border-b bg-muted/40 sm:table-header-group">
					<tr>
						<th
							scope="col"
							className="w-[30%] px-5 py-3 text-xs font-bold uppercase tracking-wide text-muted-foreground"
						>
							Feature
						</th>
						<th
							scope="col"
							className="w-[35%] bg-primary/5 px-5 py-3 text-sm font-bold text-foreground"
						>
							{usLabel}
						</th>
						<th
							scope="col"
							className="w-[35%] px-5 py-3 text-sm font-bold text-foreground"
						>
							{themLabel}
						</th>
					</tr>
				</thead>
				{groups.map((group) => (
					<tbody
						key={group.title}
						className="block sm:table-row-group"
					>
						<tr className="block border-b bg-muted/20 sm:table-row">
							<th
								scope="colgroup"
								colSpan={3}
								className="block px-5 pt-4 pb-2 text-xs font-bold uppercase tracking-wide text-primary sm:table-cell"
							>
								{group.title}
							</th>
						</tr>
						{group.rows.map((row) => (
							<tr
								key={row.feature}
								className="block border-b last:border-b-0 sm:table-row"
							>
								<th
									scope="row"
									className="block px-5 pt-4 pb-1 align-top text-sm font-semibold text-foreground sm:table-cell sm:py-4"
								>
									<span className="inline-flex items-center gap-1.5">
										{row.feature}
										{row.explainer ? (
											<HoverCard openDelay={100}>
												<HoverCardTrigger asChild>
													<button
														type="button"
														className="text-muted-foreground hover:text-foreground"
														aria-label={`About ${row.feature}`}
													>
														<InfoIcon
															className="size-3.5"
															aria-hidden
														/>
													</button>
												</HoverCardTrigger>
												<HoverCardContent className="w-72 text-sm font-normal text-muted-foreground">
													{row.explainer}
												</HoverCardContent>
											</HoverCard>
										) : null}
									</span>
								</th>
								<td className="block px-5 py-1.5 align-top sm:table-cell sm:bg-primary/5 sm:py-4">
									<span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-muted-foreground sm:hidden">
										{usLabel}
									</span>
									<MarkedCell cell={row.us} />
								</td>
								<td className="block px-5 pt-1.5 pb-4 align-top sm:table-cell sm:py-4">
									<span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-muted-foreground sm:hidden">
										{themLabel}
									</span>
									<MarkedCell cell={row.them} />
								</td>
							</tr>
						))}
					</tbody>
				))}
			</table>
			{footnote || sources.length > 0 ? (
				<div className="space-y-1 border-t px-5 py-3 text-xs text-muted-foreground">
					{footnote ? <p>{footnote}</p> : null}
					{sources.length > 0 ? (
						<p>
							Sources:{" "}
							{sources.map((source, index) => (
								<Fragment key={source.href}>
									{index > 0 ? ", " : null}
									<a
										href={source.href}
										target="_blank"
										rel="noopener noreferrer"
										className="font-semibold text-primary hover:underline"
									>
										{source.label}
									</a>
								</Fragment>
							))}
							{verified ? (
								<>
									. Checked{" "}
									<time dateTime={verified}>
										{formatLongDate(verified)}
									</time>
									.
								</>
							) : null}
						</p>
					) : null}
				</div>
			) : null}
		</div>
	);
}
